import { motion } from "framer-motion";
import { MapPin, Clock, Phone, Mail, Instagram, ArrowUpRight } from "lucide-react";
import Navbar from "@/components/home/Navbar";
import Footer from "@/components/home/Footer";

const horaires = [
    { jour: "Mardi", h: "9h30 – 12h30 · 14h – 19h" },
    { jour: "Mercredi", h: "9h30 – 12h30 · 14h – 19h" },
    { jour: "Jeudi", h: "9h30 – 12h30 · 14h – 19h" },
    { jour: "Vendredi", h: "9h30 – 12h30 · 14h – 19h" },
    { jour: "Samedi", h: "9h30 – 12h30 · 14h – 18h" },
    { jour: "Dimanche", h: "Fermé", closed: true },
    { jour: "Lundi", h: "Fermé", closed: true },
];

const canaux = [
    {
        Icon: Phone,
        label: "Téléphone",
        value: "04 74 22 71 44",
        href: "tel:+33474227144",
        bg: "#F2D580",
        fg: "#0F2A20",
        testid: "contact-phone",
    },
    {
        Icon: Mail,
        label: "Email",
        value: "contact@lapetitelunetterie.fr",
        href: "mailto:contact@lapetitelunetterie.fr",
        bg: "#DD7259",
        fg: "#FAF6EF",
        testid: "contact-email",
    },
    {
        Icon: Instagram,
        label: "Instagram",
        value: "@lapetitelunetterie_bourg",
        href: "https://instagram.com/lapetitelunetterie_bourg",
        bg: "#ECB6FF",
        fg: "#0F2A20",
        testid: "contact-instagram",
    },
];

export default function Contact() {
    return (
        <div
            className="min-h-screen font-body bg-lpl-bg text-lpl-text"
            data-testid="contact-page"
        >
            <Navbar />

            <main>
                {/* Hero */}
                <section
                    className="relative pt-28 md:pt-32 pb-12 md:pb-16 bg-lpl-bg-alt overflow-hidden"
                    data-testid="contact-hero"
                >
                    <div
                        aria-hidden
                        className="pointer-events-none absolute -top-20 -right-20 w-[420px] h-[420px] rounded-full bg-lpl-lavender/30 hidden md:block"
                    />
                    <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-16">
                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="font-serif italic text-2xl md:text-3xl text-lpl-terracotta mb-4"
                        >
                            On vous attend
                        </motion.p>
                        <motion.h1
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            className="font-display font-semibold text-6xl md:text-8xl text-lpl-text leading-[0.9] tracking-tight max-w-4xl"
                            data-testid="contact-title"
                        >
                            Venez nous voir{" "}
                            <span className="font-serif italic font-normal text-lpl-primary-deep">
                                à Bourg-en-Bresse
                            </span>
                            <span className="font-serif italic font-normal text-lpl-lavender">
                                .
                            </span>
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className="mt-8 max-w-2xl text-lg md:text-xl text-lpl-text-muted leading-relaxed"
                        >
                            La porte est toujours ouverte, le café est prêt et
                            on a hâte de vous rencontrer. Passez quand vous
                            voulez, ou envoyez-nous un mot.
                        </motion.p>
                    </div>
                </section>

                {/* Map + Adresse / Horaires */}
                <section
                    className="relative py-20 md:py-28 bg-lpl-bg"
                    data-testid="contact-info-section"
                >
                    <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
                        {/* Map */}
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.7 }}
                            className="lg:col-span-7 relative"
                        >
                            <div
                                aria-hidden
                                className="absolute -bottom-5 -left-5 w-full h-full bg-lpl-primary"
                            />
                            <div
                                className="relative h-[440px] md:h-[560px] overflow-hidden shadow-xl"
                                data-testid="contact-map"
                            >
                                <iframe
                                    title="La Petite Lunetterie sur la carte"
                                    src="https://www.google.com/maps?q=7+place+Edgar+Quinet+Bourg-en-Bresse&t=&z=17&ie=UTF8&iwloc=&output=embed"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    allowFullScreen
                                ></iframe>
                            </div>
                            <a
                                href="https://www.google.com/maps/dir/?api=1&destination=7+place+Edgar+Quinet+Bourg-en-Bresse"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-itineraire"
                                className="mt-6 inline-flex items-center gap-3 font-serif italic text-xl text-lpl-primary-deep underline decoration-2 decoration-lpl-yellow underline-offset-[6px] hover:text-lpl-primary-deep transition-colors"
                            >
                                Calculer mon itinéraire
                                <ArrowUpRight size={20} />
                            </a>
                        </motion.div>

                        {/* Adresse + Horaires */}
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            className="lg:col-span-5 space-y-10"
                        >
                            <div>
                                <div className="flex items-center gap-2 mb-3 text-lpl-text-muted">
                                    <MapPin size={16} />
                                    <span className="text-xs uppercase tracking-[0.18em] font-semibold">
                                        Adresse
                                    </span>
                                </div>
                                <p
                                    className="font-display font-semibold text-3xl md:text-4xl text-lpl-text leading-tight tracking-tight"
                                    data-testid="contact-adresse"
                                >
                                    La Petite Lunetterie
                                    <br />
                                    <span className="font-serif italic font-normal text-lpl-primary-deep">
                                        7 place Edgar Quinet
                                    </span>
                                </p>
                                <p className="mt-2 text-lpl-text-muted">
                                    01000 Bourg-en-Bresse, France
                                </p>
                            </div>

                            <div>
                                <div className="flex items-center gap-2 mb-4 text-lpl-text-muted">
                                    <Clock size={16} />
                                    <span className="text-xs uppercase tracking-[0.18em] font-semibold">
                                        Horaires
                                    </span>
                                </div>
                                <ul
                                    className="divide-y divide-lpl-border"
                                    data-testid="contact-horaires"
                                >
                                    {horaires.map((h) => (
                                        <li
                                            key={h.jour}
                                            className={`flex items-center justify-between py-3 ${
                                                h.closed
                                                    ? "text-lpl-text-muted"
                                                    : "text-lpl-text"
                                            }`}
                                        >
                                            <span className="font-display font-semibold text-base md:text-lg">
                                                {h.jour}
                                            </span>
                                            <span
                                                className={`font-body text-base ${
                                                    h.closed
                                                        ? "italic"
                                                        : ""
                                                }`}
                                            >
                                                {h.h}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* Canaux de contact */}
                <section
                    className="relative py-20 md:py-28 bg-lpl-bg-alt"
                    data-testid="contact-canaux-section"
                >
                    <div className="max-w-7xl mx-auto px-5 md:px-10">
                        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
                            <h2
                                className="font-display font-semibold text-5xl md:text-7xl text-lpl-text leading-[0.95] tracking-tight max-w-2xl"
                                data-testid="contact-canaux-title"
                            >
                                Une question ?{" "}
                                <span className="font-serif italic font-normal text-lpl-primary-deep">
                                    Écrivez-nous.
                                </span>
                            </h2>
                            <p className="md:max-w-sm text-lpl-text-muted text-base md:text-lg leading-relaxed">
                                On répond dans la journée (sauf dimanche et
                                lundi, on prend l'air).
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            {canaux.map((c, i) => {
                                const Icon = c.Icon;
                                return (
                                    <motion.a
                                        key={c.label}
                                        href={c.href}
                                        target={
                                            c.href.startsWith("http")
                                                ? "_blank"
                                                : undefined
                                        }
                                        rel={
                                            c.href.startsWith("http")
                                                ? "noopener noreferrer"
                                                : undefined
                                        }
                                        initial={{ opacity: 0, y: 24 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{
                                            once: true,
                                            margin: "-80px",
                                        }}
                                        transition={{
                                            duration: 0.55,
                                            delay: i * 0.1,
                                        }}
                                        data-testid={c.testid}
                                        style={{
                                            background: c.bg,
                                            color: c.fg,
                                        }}
                                        className="group relative p-8 md:p-10 min-h-[220px] flex flex-col justify-between overflow-hidden hover:translate-y-[-4px] transition-transform duration-300"
                                    >
                                        <div className="flex items-start justify-between">
                                            <Icon
                                                size={36}
                                                strokeWidth={1.5}
                                            />
                                            <ArrowUpRight
                                                size={22}
                                                className="opacity-70 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                                            />
                                        </div>
                                        <div>
                                            <p className="text-xs uppercase tracking-[0.18em] font-semibold opacity-70 mb-2">
                                                {c.label}
                                            </p>
                                            <p
                                                className={
                                                    c.testid === "contact-phone"
                                                        ? "font-display font-semibold text-2xl md:text-3xl leading-tight tracking-tight"
                                                        : "font-display font-semibold text-base md:text-lg leading-tight tracking-tight whitespace-nowrap"
                                                }
                                            >
                                                {c.value}
                                            </p>
                                        </div>
                                    </motion.a>
                                );
                            })}
                        </div>

                        <p className="mt-16 text-center font-serif italic text-2xl md:text-3xl text-lpl-text-muted">
                            ou venez nous voir, on vous garde le meilleur café.
                        </p>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
